/** Shared GLSL utility snippets.
 *
 *  Each export is a GLSL source string meant to be template-interpolated into
 *  a shader's fragment source before the main() body.
 *
 *  Usage:  `... ${uvToSphereGlsl} ${sphereToUvGlsl} void main() { ... }`
 */

/** vec3 uvToSphere(vec2 uv) — equirectangular UV → unit-sphere point.
 *  Shared by brush, envelope, stamp, blur, and composite shaders. */
export const uvToSphereGlsl = `
  vec3 uvToSphere(vec2 uv) {
    float theta = (1.0 - uv.x) * 6.28318530718 + 3.14159265359;
    float phi   = (1.0 - uv.y) * 3.14159265359;
    return vec3(sin(phi)*cos(theta), cos(phi), sin(phi)*sin(theta));
  }
`;

/** vec2 sphereToUV(vec3 p) — unit-sphere point → equirectangular UV.
 *  Inverse of uvToSphere. Used by blur and composite shaders. */
export const sphereToUvGlsl = `
  vec2 sphereToUV(vec3 p) {
    float phi   = acos(clamp(p.y, -1.0, 1.0));
    float theta = atan(p.z, p.x);
    float u = 1.0 - (theta - 3.14159265359) / 6.28318530718;
    float v = 1.0 - phi / 3.14159265359;
    return fract(vec2(u, v));
  }
`;

/** vec2 applyInverseWarp(vec2 ndc) — sphere-locked radial warp for inverse mode.
 *  Requires uniforms: uSphereCenterNDC (vec2), uInverseWarpK (float),
 *  uInverseWarpStrength (float). Used by brush, envelope, and stamp shaders. */
export const applyInverseWarpGlsl = `
  vec2 applyInverseWarp(vec2 ndc) {
    vec2 sv = ndc - uSphereCenterNDC;
    float r = length(sv);
    vec2 sd = r > 1e-6 ? (sv / r) : vec2(1.0, 0.0);
    float rc = clamp(r, 0.0, 1.0);
    float inRw  = 1.0 - pow(max(1.0 - rc, 0.0), uInverseWarpK);
    float outRw = pow(rc, uInverseWarpK);
    float em    = smoothstep(0.2, 0.95, rc);
    float wr    = clamp(rc + (mix(inRw, outRw, em) - rc) * uInverseWarpStrength, 0.0, 1.5);
    return uSphereCenterNDC + sd * wr;
  }
`;
